import React, { useState } from 'react';
import ContactBlock from './ContactBlock';
import heroImage from '../assets/Hero-image.webp';
import './Home.css';

const FAQS = [
  [
    'Do I need to know anything about AI?',
    "No. That's our job. We ask about your business — how work comes in, who touches it, where it stalls — and translate that into a recommendation in plain language.",
  ],
  [
    'What does an assessment cost?',
    "The first call is free. If a deeper review makes sense, it's a fixed fee agreed up front, and it's credited toward a build if you go ahead.",
  ],
  [
    "What if AI isn't the answer for us?",
    "Then we say so. Often the win is a better internal tool, cleaner data, or removing a step — not a model. We'd rather be the firm you trust than the one that sold you something.",
  ],
  [
    'How long until we see something working?',
    'A recommendation inside a week. A first working build typically lands in four to ten weeks, with a demo you can click every week along the way.',
  ],
  [
    'Who owns what we build?',
    'You do — the code, the accounts, the data. No lock-in, and nothing that stops another team taking over later.',
  ],
];

const SERVICES = [
  {
    step: '01 / assess',
    title: 'AI readiness review',
    body: 'We look at how you work and come back with a short list of what AI would genuinely improve — and what it wouldn’t.',
    detail: ['process mapping', 'tool audit', 'opportunity shortlist'],
  },
  {
    step: '02 / automate',
    title: 'Workflow automation',
    body: 'Quoting, scheduling, intake, follow-ups, reporting — the repetitive work that eats your week, handled.',
    detail: ['document handling', 'customer follow-up', 'reporting & alerts'],
  },
  {
    step: '03 / build',
    title: 'Custom web & mobile',
    body: 'The portals, booking systems, and internal tools that hold it all together — built to fit, not adapted to.',
    detail: ['customer portals', 'iOS & Android apps', 'internal dashboards'],
  },
  {
    step: '04 / support',
    title: 'Training & upkeep',
    body: 'We teach your team to use it, keep it running, and improve it as your business changes.',
    detail: ['team training', 'monitoring & fixes', 'quarterly reviews'],
  },
];

const PROCESS_STEPS = [
  ['01', 'Assess', 'A free call, then a map of where time and money leak.'],
  ['02', 'Recommend', 'A short plan: what to automate, and what to leave alone.'],
  ['03', 'Build', 'Fixed scope, weekly demos, no surprise invoices.'],
  ['04', 'Support', 'Training, upkeep, and the next improvement.'],
];

const WORK = [
  {
    tag: 'automation / trades',
    title: 'Quoting that used to take a day now takes ten minutes',
    body: '[ Placeholder case study — replace with a real client story. ]',
    metric: 'result metric here',
  },
  {
    tag: 'portal / services',
    title: 'One place for clients instead of six email threads',
    body: '[ Placeholder case study — replace with a real client story. ]',
    metric: 'result metric here',
  },
  {
    tag: 'mobile / retail',
    title: 'An app the team in the field actually opens',
    body: '[ Placeholder case study — replace with a real client story. ]',
    metric: 'result metric here',
  },
];

const PROBLEMS = [
  "Vendors selling AI tools that solve a problem you don't have.",
  "A stack of subscriptions that don't talk to each other, and a team retyping the same data into all of them.",
  "Quotes from agencies written in a language you're not expected to understand.",
];

/**
 * Landing page.
 *
 * The `show*` props gate the three sections whose content is still
 * placeholder in the design (client logos, case studies, testimonial).
 * Flip them on in App.js once there is real content to put in them.
 *
 * `processStyle` matches the design's toggle: "terminal" or "plain".
 */
const Home = ({
  showLogoBar = false,
  showWork = false,
  showTestimonial = false,
  processStyle = 'terminal',
}) => {
  const [openFaq, setOpenFaq] = useState(0);

  const toggleFaq = (index) => {
    setOpenFaq((current) => (current === index ? -1 : index));
  };

  const scrollTo = (e, targetId) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (!target) return;
    const yPosition = target.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top: yPosition, behavior: 'smooth' });
  };

  return (
    <main className="ak-home">
      {/* ===== Hero ===== */}
      <header id="top" className="ak-hero">
        <img
          src={heroImage}
          alt="Akeyreu's founder in the office, with the team working at desks behind."
          className="ak-hero-photo"
        />
        <div className="ak-hero-scrim" aria-hidden="true" />
        <div className="ak-hero-copy">
          <p className="ak-eyebrow">// AI consulting &amp; custom software, est. 2023</p>
          <h1 className="ak-hero-title">
            Figure out where AI actually helps. Then we build it.
          </h1>
          <p className="ak-hero-lede">
            Most small businesses don't need an AI strategy — they need one or two
            things automated well. We help you find those, then build the software
            that runs them.
          </p>
          <div className="ak-hero-actions">
            <a href="#contact" className="ak-btn" onClick={(e) => scrollTo(e, 'contact')}>
              Book a free AI assessment
            </a>
            <a href="#process" className="ak-btn-ghost" onClick={(e) => scrollTo(e, 'process')}>
              how_it_works →
            </a>
          </div>
          <p className="ak-hero-note">30 minutes · no cost · no obligation</p>
        </div>
      </header>

      {/* ===== Logo bar ===== */}
      {showLogoBar && (
        <section className="ak-logobar">
          <span className="ak-logobar-label">// trusted by</span>
          <div className="ak-logobar-marks">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className="ak-logobar-mark">[ client logo ]</span>
            ))}
          </div>
        </section>
      )}

      {/* ===== Problem ===== */}
      <section className="ak-problem">
        <div>
          <p className="ak-eyebrow">// the problem</p>
          <h2 className="ak-h2">
            You've been told AI will change your business. Nobody told you where to start.
          </h2>
        </div>
        <div className="ak-problem-list">
          {PROBLEMS.map((text) => (
            <div key={text} className="ak-problem-row">
              <span className="ak-problem-x">✗</span>
              <p>{text}</p>
            </div>
          ))}
          <div className="ak-problem-row ak-problem-row-answer">
            <span className="ak-problem-arrow">→</span>
            <p>
              We start by telling you what <em>not</em> to buy.
            </p>
          </div>
        </div>
      </section>

      {/* ===== Services ===== */}
      <section id="services" className="ak-services">
        <p className="ak-eyebrow">// what we do</p>
        <h2 className="ak-h2 ak-services-title">
          Advice you can act on, and the software to act on it.
        </h2>
        <div className="ak-services-grid">
          {SERVICES.map((service) => (
            <article key={service.step} className="ak-service">
              <div className="ak-service-step">{service.step}</div>
              <h3>{service.title}</h3>
              <p className="ak-service-body">{service.body}</p>
              <p className="ak-service-detail">
                {service.detail.map((line, i) => (
                  <React.Fragment key={line}>
                    {i > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* ===== Process ===== */}
      <section id="process" className="ak-process">
        <div>
          <p className="ak-eyebrow">// process</p>
          <h2 className="ak-h2 ak-process-title">How an engagement goes</h2>
          <p className="ak-process-lede">
            You won't need to speak developer, and you won't need to know anything
            about AI. We start with advice, and only build once it's clear what's
            worth building.
          </p>
          <div className="ak-promises">
            {[
              'Fixed scope and a fixed price before we start.',
              'You own the code and the accounts. Always.',
              'One person you talk to, start to finish.',
            ].map((promise) => (
              <div key={promise} className="ak-promise">
                <span>→</span>
                <p>{promise}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="ak-process-main">
          {processStyle === 'plain' ? (
            <div className="ak-steps">
              {PROCESS_STEPS.map(([num, title, body]) => (
                <div key={num} className="ak-step">
                  <span className="ak-step-num">{num}</span>
                  <div>
                    <h3>{title}</h3>
                    <p>{body}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="ak-terminal">
              <p className="ak-terminal-cmd">
                <span className="ak-terminal-prompt">$</span>{' '}
                <span className="ak-terminal-input">engagement --steps</span>
              </p>
              <p>
                <span className="ak-terminal-ok">✓</span> 01_assess&nbsp;&nbsp;&nbsp;— free
                call, then a map of where time and money leak
              </p>
              <p>
                <span className="ak-terminal-ok">✓</span> 02_recommend — a short plan: what
                to automate, what to leave alone
              </p>
              <p>
                <span className="ak-terminal-ok">✓</span> 03_build&nbsp;&nbsp;&nbsp;&nbsp;—
                fixed scope, weekly demos, no surprise invoices
              </p>
              <p>
                <span className="ak-terminal-ok">✓</span> 04_support&nbsp;&nbsp;— training,
                upkeep, and the next improvement
                <span className="ak-caret" />
              </p>
            </div>
          )}

          <div className="ak-stats">
            <div className="ak-stat">
              <div className="ak-stat-value">1 week</div>
              <div className="ak-stat-label">to a recommendation</div>
            </div>
            <div className="ak-stat">
              <div className="ak-stat-value">4–10 weeks</div>
              <div className="ak-stat-label">typical first build</div>
            </div>
            <div className="ak-stat">
              <div className="ak-stat-value">Weekly</div>
              <div className="ak-stat-label">demos, every week</div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Selected work ===== */}
      {showWork && (
        <section className="ak-work">
          <p className="ak-eyebrow">// selected work</p>
          <h2 className="ak-h2 ak-work-title">What this looks like in practice</h2>
          <div className="ak-work-grid">
            {WORK.map((item) => (
              <article key={item.tag} className="ak-work-card">
                <div className="ak-work-shot">
                  <span className="ak-placeholder-label">[ project screenshot ]</span>
                </div>
                <div className="ak-work-body">
                  <div className="ak-work-tag">{item.tag}</div>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                  <p className="ak-work-metric">{item.metric}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* ===== Testimonial ===== */}
      {showTestimonial && (
        <section className="ak-testimonial-wrap">
          <blockquote className="ak-testimonial">
            <p>
              “Client testimonial goes here — a sentence or two from a business owner
              you've helped.”
            </p>
            <footer>[ placeholder — send a real quote, name &amp; company ]</footer>
          </blockquote>
        </section>
      )}

      {/* ===== FAQ ===== */}
      <section id="faq" className="ak-faq">
        <div>
          <p className="ak-eyebrow">// questions</p>
          <h2 className="ak-h2">Straight answers</h2>
        </div>
        <div className="ak-faq-list">
          {FAQS.map(([question, answer], index) => {
            const isOpen = openFaq === index;
            return (
              <div key={question} className="ak-faq-item">
                <h3 className="ak-faq-heading">
                  <button
                    type="button"
                    className="ak-faq-question"
                    onClick={() => toggleFaq(index)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <span>{question}</span>
                    <span className="ak-faq-sign">{isOpen ? '−' : '+'}</span>
                  </button>
                </h3>
                {isOpen && (
                  <p id={`faq-answer-${index}`} className="ak-faq-answer">
                    {answer}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ===== Contact ===== */}
      <ContactBlock
        id="contact"
        title="Start with a conversation, not a contract."
        lede="A free 30-minute call. We'll tell you honestly whether AI or custom software is worth it for your business — including when the answer is no."
      />
    </main>
  );
};

export default Home;
